<template>
  <dashboard-layout title="Location" :kicker="kickerLabel" :items="navItems">
    <partner-pin-location-modal
      ref="pinModal"
      modal-id="PartnerLocationPickerModal"
      map-element-id="partner-location-map"
      @confirmed="onPinConfirmed"
    />

    <div class="bg-white rounded-3 border shadow-sm p-3 p-md-4">
      <p class="inter-regular-family text-muted text-full-11 mb-3">
        Set your pin location for <strong>{{ countryNameLabel }}</strong>. This is saved per country and used to show salon/groomer
        results for the selected country.
      </p>

      <div v-if="loadError" class="alert alert-warning py-2 px-3 small mb-3" role="alert">
        {{ loadError }}
      </div>

      <div v-if="loading" class="text-center py-5 inter-regular-family text-muted">
        Loading location…
      </div>

      <form v-if="!loading" @submit.prevent="save">
        <div class="row g-2">
          <div class="col-12">
            <label class="pl-label">Address</label>
            <input
              v-model.trim="location.address"
              type="text"
              class="pl-input"
              placeholder="Search for area, street name, landmark..."
              @click="initMap"
              readonly
              required
            >
          </div>

          <div class="col-md-6">
            <label class="pl-label">Latitude</label>
            <input v-model.number="location.lat" type="number" step="0.000001" class="pl-input" placeholder="25.204849" readonly required>
          </div>
          <div class="col-md-6">
            <label class="pl-label">Longitude</label>
            <input v-model.number="location.lng" type="number" step="0.000001" class="pl-input" placeholder="55.270783" readonly required>
          </div>

          <div class="col-12 d-flex flex-wrap gap-2 mt-2">
            <button
              type="button"
              class="btn btn-outline-secondary btn-sm"
              @click="initMap"
            >
              Open on map
            </button>
            <button type="button" class="btn btn-outline-secondary btn-sm" @click="useBrowserLocation">
              Use my current location
            </button>
          </div>
        </div>

        <button type="submit" class="pl-save inter-bold-family text-uppercase text-full-11 mt-3" :disabled="saving">
          {{ saving ? 'Saving…' : 'Save location' }}
        </button>
      </form>
    </div>
  </dashboard-layout>
</template>

<script>
/* global google */
import DashboardLayout from './DashboardLayout.vue';
import PartnerPinLocationModal from './PartnerPinLocationModal.vue';
import { SALON_DASH_NAV, GROOMER_DASH_NAV } from '@/utils/dashboardNav';
import { readCountrySlugFromStorage, readCountryNameFromStorage } from '@/utils/currentCountry';
import { readPartnerAuth, clearPartnerSession } from '@/utils/partnerProfileStorage';

function emptyLocation(countrySlug) {
  return {
    country_slug: String(countrySlug || '').toLowerCase(),
    address: '',
    lat: null,
    lng: null,
  };
}

export default {
  name: 'PartnerDashboardLocation',
  components: { DashboardLayout, PartnerPinLocationModal },
  props: {
    partnerRole: {
      type: String,
      required: true,
      validator: (v) => v === 'salon' || v === 'groomer',
    },
  },
  data() {
    return {
      loading: true,
      saving: false,
      loadError: '',
      countrySlug: '',
      countryName: '',
      location: emptyLocation(''),
    };
  },
  computed: {
    kickerLabel() {
      return this.partnerRole === 'salon' ? 'Salon dashboard' : 'Groomer dashboard';
    },
    navItems() {
      return this.partnerRole === 'salon' ? SALON_DASH_NAV : GROOMER_DASH_NAV;
    },
    countryNameLabel() {
      return (this.countryName || '').trim() || this.countrySlug || 'your country';
    },
    mapUrl() {
      const lat = Number(this.location.lat);
      const lng = Number(this.location.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return '';
      return `https://www.google.com/maps?q=${encodeURIComponent(`${lat},${lng}`)}`;
    },
  },
  async mounted() {
    const auth = readPartnerAuth();
    if (!auth?.joinType || !auth.email || !auth.token) {
      this.$router.replace({ path: '/login' });
      return;
    }
    const jt = String(auth.joinType).toLowerCase();
    if (jt !== this.partnerRole) {
      this.$router.replace({ path: `/dashboard/${jt}` });
      return;
    }
    await this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.loadError = '';
      const selectedSlug = readCountrySlugFromStorage();
      this.countrySlug = selectedSlug;
      this.countryName = readCountryNameFromStorage();
      this.location = emptyLocation(selectedSlug);

      try {
        const res = await this.$axios.get('/partner/location');
        if (res?.data?.success && res.data.location) {
          const loc = res.data.location || {};
          // Locked to the partner's own country returned by API.
          this.countrySlug = String(res.data.country_slug || selectedSlug || '').toLowerCase();
          this.location = {
            country_slug: String(this.countrySlug || '').toLowerCase(),
            address: String(loc.address || ''),
            lat: loc.lat != null ? Number(loc.lat) : null,
            lng: loc.lng != null ? Number(loc.lng) : null,
          };
        }
      } catch (e) {
        const status = e?.response?.status;
        if (status === 401) {
          clearPartnerSession();
          this.$router.replace({ path: '/login' });
          return;
        }
        this.loadError = e?.response?.data?.message || 'Could not load location from server.';
      } finally {
        this.loading = false;
      }
    },
    async save() {
      const lat = Number(this.location.lat);
      const lng = Number(this.location.lng);
      if (!this.location.address.trim()) {
        this.$toast?.error('Please enter address.');
        return;
      }
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        this.$toast?.error('Please enter valid latitude and longitude.');
        return;
      }

      this.saving = true;
      try {
        const res = await this.$axios.patch('/partner/location', {
          location: { address: this.location.address, lat, lng },
        });
        if (res?.data?.success) {
          this.$toast?.success('Location saved.');
        }
      } catch (e) {
        const status = e?.response?.status;
        if (status === 401) {
          clearPartnerSession();
          this.$router.replace({ path: '/login' });
          return;
        }
        const errs = e?.response?.data?.errors;
        const msg = e?.response?.data?.message || 'Could not save location.';
        if (errs && typeof errs === 'object') {
          const firstKey = Object.keys(errs)[0];
          const first = Array.isArray(errs[firstKey]) ? errs[firstKey][0] : '';
          this.$toast?.error(first || msg);
        } else {
          this.$toast?.error(msg);
        }
      } finally {
        this.saving = false;
      }
    },
    useBrowserLocation() {
      if (!navigator.geolocation) {
        this.$toast?.error('Geolocation is not supported in this browser.');
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = pos?.coords?.latitude;
          const lng = pos?.coords?.longitude;
          if (lat != null && lng != null) {
            this.location.lat = Number(lat);
            this.location.lng = Number(lng);
            this.fillAddressFromCoords(this.location.lat, this.location.lng);
          }
        },
        () => {
          this.$toast?.error('Could not read your current location.');
        },
        { enableHighAccuracy: true, timeout: 12000 }
      );
    },

    initMap() {
      this.$refs.pinModal?.open({
        lat: this.location.lat,
        lng: this.location.lng,
        address: this.location.address,
      });
    },
    onPinConfirmed({ address, lat, lng }) {
      this.location.address = address;
      this.location.lat = lat;
      this.location.lng = lng;
      this.$toast?.success('Location selected!');
    },
    fillAddressFromCoords(lat, lng) {
      if (!window.google?.maps?.Geocoder) return;
      let countryCode = '';
      try {
        const stored = JSON.parse(localStorage.getItem('selected_country') || 'null');
        countryCode = (stored?.code || '').toString().trim();
      } catch (_) {
        countryCode = '';
      }
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode(
        {
          location: { lat: Number(lat), lng: Number(lng) },
          ...(countryCode ? { region: countryCode } : {}),
        },
        (results, status) => {
          if (status === 'OK' && results && results[0]) {
            this.location.address = results[0].formatted_address || '';
          }
        }
      );
    },
  },
};
</script>

<style scoped>
.pl-label {
  font-size: 11px;
  display: block;
  margin-bottom: 4px;
  font-family: interregular, sans-serif;
  color: #333;
}
.pl-input {
  width: 100%;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 13px;
}
.pl-save {
  border: none;
  border-radius: 10px;
  padding: 12px 28px;
  background: #f8721f;
  color: #fff;
}
.pl-save:disabled {
  opacity: 0.65;
}
</style>

